// Story 7.12 (ADO-911) — the ONE adapter from a recommendation row to the shared
// card's `ProductSummary` shape.
//
// Shared rather than copied (PR #168 review, 2026-07-30). Both recommendation
// surfaces mount the real grid `ProductCard` — the PDP carousel
// (`RecommendationRail`) and the Linked Product upsell modal (`UpsellModal`) —
// and each had its own identical copy of this function, with a comment pointing
// at the other one.
//
// That duplication is not cosmetic: the STUB CONTRACT is depended on elsewhere.
// `useProductDetailStore.seed`/`load` document the shape verbatim ("carries no
// `description` and no `unitOfMeasure` — they are hard-set to null, never
// invented") and the `partial` repair exists precisely because of it. With two
// copies, teaching one surface to populate `unitOfMeasure` would silently stop
// the repair being needed there while leaving the other unchanged — the same
// class of coupling that produced the stub-pins-`loaded` bug (PR #168).

import type { ProductSummary } from '../api/browse.client';
import type { RecommendedProduct } from '../api/recommendations.client';

/**
 * Adapt a recommendation row to the shared card's `ProductSummary` shape. The
 * recommendation DTO deliberately carries only sku/name/imageUrl/packSize and
 * isPickedFresh (no price, D-1); the fields the card also reads are genuinely absent here, so
 * they are `null` rather than invented. `id` mirrors the SKU: the card uses it
 * only as an identity, and the SKU is the identity on this surface.
 *
 * The result is therefore a STUB, not a read-model row. Every surface that
 * renders one must pass `partialRow` to `ProductCard` so the PDP handover says
 * so and `useProductDetailStore.load` resolves the real row.
 */
export function toProductSummary(item: RecommendedProduct): ProductSummary {
  return {
    id: item.sku,
    sku: item.sku,
    name: item.name,
    description: null,
    packSize: item.packSize,
    unitOfMeasure: null,
    imageUrl: item.imageUrl,
    isPickedFresh: item.isPickedFresh,
  };
}
